import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Clock, AlertTriangle, Hourglass } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 8;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ status: 'polling', message: 'Confirming your payment…' });
  const [details, setDetails] = useState(null);
  const attempts = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'error', message: 'Missing payment session id.' });
      return;
    }

    let cancelled = false;

    async function poll() {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === 'paid') {
          setState({ status: 'paid', message: 'Payment confirmed — entitlements activated.' });
          return;
        }
        if (data.status === 'expired') {
          setState({ status: 'expired', message: 'Payment session expired. Please try again.' });
          return;
        }
        if (attempts.current >= MAX_POLLS) {
          setState({ status: 'timeout', message: 'Still processing — check your email for confirmation. Your dashboard will update once it clears.' });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setState({ status: 'error', message: e.response?.data?.detail || 'Could not check payment status.' });
      }
    }

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const meta = details?.metadata || {};
  const isTier = meta.purpose === 'tier_subscription';
  const isAddon = meta.purpose === 'payg_topup';

  const StatusIcon = (
    state.status === 'paid' ? CheckCircle2 :
    state.status === 'polling' ? Clock :
    state.status === 'timeout' ? Hourglass :
    AlertTriangle
  );
  const iconBg =
    state.status === 'paid' ? 'bg-[var(--vh-success)]/15 text-[var(--vh-success)]' :
    state.status === 'polling' ? 'bg-[var(--vh-bg-muted)] text-[var(--vh-brand-primary)]' :
    state.status === 'timeout' ? 'bg-[var(--vh-warning)]/40 text-[var(--vh-brand-primary)]' :
    'bg-[var(--vh-accent)]/15 text-[var(--vh-accent)]';

  return (
    <div className="min-h-screen bg-[var(--vh-bg-base)] text-[var(--vh-text-primary)] flex items-center justify-center px-4 py-10" data-testid="payment-success-page">
      <div className="max-w-lg w-full bg-[var(--vh-bg-surface)] rounded-3xl border border-[var(--vh-border)] shadow-sm p-9 text-center">
        <div className={`inline-flex w-14 h-14 rounded-2xl items-center justify-center mb-5 ${iconBg}`} data-testid="payment-status-icon">
          <StatusIcon className={`w-7 h-7 ${state.status === 'polling' ? 'animate-pulse' : ''}`} />
        </div>
        <h1 className="font-display text-3xl mb-2 text-[var(--vh-text-primary)]" data-testid="payment-status-title">
          {state.status === 'paid' && 'Thank you!'}
          {state.status === 'polling' && 'Just a moment…'}
          {state.status === 'expired' && 'Session expired'}
          {state.status === 'timeout' && 'Still processing'}
          {state.status === 'error' && 'Something went wrong'}
        </h1>
        <p className="text-[var(--vh-text-secondary)] mb-7 leading-relaxed" data-testid="payment-status-message">{state.message}</p>

        {state.status === 'paid' && (
          <div className="text-left bg-[var(--vh-bg-muted)] border border-[var(--vh-border)] rounded-2xl p-5 mb-7 text-sm space-y-2">
            {isTier && (
              <>
                <div className="flex justify-between"><span className="text-[var(--vh-text-muted)]">Plan</span><strong className="text-[var(--vh-text-primary)]">{meta.tier_name}</strong></div>
                <div className="flex justify-between"><span className="text-[var(--vh-text-muted)]">Billing</span><strong className="text-[var(--vh-text-primary)] capitalize">{meta.period}</strong></div>
              </>
            )}
            {isAddon && (
              <>
                <div className="flex justify-between"><span className="text-[var(--vh-text-muted)]">Pack</span><strong className="text-[var(--vh-text-primary)]">{meta.package_name}</strong></div>
                <div className="flex justify-between"><span className="text-[var(--vh-text-muted)]">Credits added</span><strong className="text-[var(--vh-text-primary)]">{meta.reference_credits}</strong></div>
              </>
            )}
            {details?.amount_total != null && (
              <div className="flex justify-between">
                <span className="text-[var(--vh-text-muted)]">Charged</span>
                <strong className="text-[var(--vh-text-primary)]">{(details.amount_total / 100).toFixed(2)} {details.currency?.toUpperCase()}</strong>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-3 justify-center flex-wrap">
          <button
            data-testid="back-to-pricing-btn"
            onClick={() => navigate('/pricing')}
            className="px-5 py-2.5 rounded-full border border-[var(--vh-border)] text-[var(--vh-brand-primary)] hover:bg-[var(--vh-bg-muted)] text-sm font-medium"
          >
            Back to pricing
          </button>
          <button
            data-testid="go-dashboard-btn"
            onClick={() => navigate('/')}
            className="px-5 py-2.5 rounded-full bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)] text-sm font-semibold"
          >
            Go to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
